import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IPageInfo } from '../../models/page.types';
import { AccountTypeEnum, getAccountTypeId, getAccountTypeLabel, IUser, IUsersResponse } from '../../models/user.types';
import { useLayoutContext } from '../../Context/Layout.context';
import { getUsersApi } from '../../services/users/users.service';
import { filtersActions } from '../../reducers/filtersReducer/filtersSlice';
import { DataInfoType, defaultUsersFilter } from '../../reducers/filtersReducer/filterSlice.types';
import { AxiosError } from 'axios';
import { getErrorPageUrl, Paths } from '../../utils/routerUrls';
import { useHistory, useLocation } from 'react-router-dom';
import FilterStateGroup from '../../models/filterStateGroup.types';
import {
  getExamCommitteeId,
  getExamCommitteeNameFromIdString,
} from '../SampleAssessmentRepo/Helpers/AccountTypeRendering';
import _ from 'lodash';
import { RootState } from '../../store';
import { ExaminersSortBy, UsersSortBy } from './Users.types';
import { IndexedStrings } from '../../components/SelectableTable/SelectableTable.types';
import { SignalRContext } from '../../Context/SignalR.context';
import { CallbackMethodNames } from '../../models/signalR.types';

export const useUsersTableData = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const route = useLocation();
  const { showLoader } = useLayoutContext();

  const [, setIsEmptyResultMessage] = useState<boolean>(false);
  const [previousRoute, setPreviousRoute] = useState<string>(route.pathname);

  const examinersRoute = route.pathname === Paths.examiners;
  const infoType = examinersRoute ? DataInfoType.ExaminersInfo : DataInfoType.UserInfo;

  const userDataInfo = useSelector((state: RootState) => state.filters[infoType]);

  const [isDescending, setIsDescending] = useState<boolean>(userDataInfo.filterData.sort.isDescending);
  const [sortByColumnId, setSortByColumnId] = useState<UsersSortBy>(
    infoType === DataInfoType.ExaminersInfo ? 'lastName' : (userDataInfo.filterData.sort.sortByColumnId as UsersSortBy),
  );
  const [sortExaminersByColumnId, setSortExaminersByColumnId] = useState<ExaminersSortBy>(
    infoType === DataInfoType.ExaminersInfo
      ? (userDataInfo.filterData.sort.sortByColumnId as ExaminersSortBy)
      : 'lastName',
  );

  const [filterState, setFilterState] = useState<FilterStateGroup[]>([]);
  const [filter, setFilter] = useState<IndexedStrings>(userDataInfo.filterData.filter);
  const [isValidFilter, setIsValidFilter] = useState<boolean>(true);

  const [accountTypeFilter, setAccountTypeFilter] = useState<string[]>(userDataInfo.filterData.filter.accountTypeName);
  const [examCommitteeFilter, setExamCommitteeFilter] = useState<string[]>(
    userDataInfo.filterData.filter.examCommittee,
  );
  const [accountStatusFilter, setAccountStatusFilter] = useState<string[]>(
    userDataInfo.filterData.filter.accountStatus,
  );
  const [statusFilter, setStatusFilter] = useState<string[]>(userDataInfo.filterData.filter.status);

  const [users, setUsers] = useState<IUser[]>([]);
  const [reload, setReload] = useState<boolean>(false);

  const handleCurrentPage = (response: IPageInfo) => {
    if (response.totalCount < userDataInfo.filterData.pageInfo.pageSize) {
      dispatch(
        filtersActions.updateFilter({
          filterType: infoType,
          newValues: {
            pageInfo: {
              ...response,
              currentPage: 0,
            },
          },
        }),
      );
    } else {
      dispatch(
        filtersActions.updateFilter({
          filterType: infoType,
          newValues: {
            pageInfo: {
              ...response,
            },
          },
        }),
      );
    }
  };

  const getFilterValues = (forceAccountType?: AccountTypeEnum): (string | string[])[] => {
    return Object.keys(filter).map((key) => {
      if (forceAccountType && key === 'accountTypeName') {
        return forceAccountType.toString();
      } else if (Array.isArray(filter[key])) {
        return (filter[key] as string[]).join(',');
      }
      return filter[key];
    });
  };

  const getUsers = (forceAccountType?: AccountTypeEnum) => {
    const filters = getFilterValues(forceAccountType);
    if (!isValidFilter) return;

    const abortController = new AbortController();
    const { signal } = abortController;
    showLoader(true);
    getUsersApi(
      signal ?? undefined,
      userDataInfo.filterData.pageInfo.pageSize,
      userDataInfo.filterData.pageInfo.currentPage,
      examinersRoute ? sortExaminersByColumnId : sortByColumnId,
      isDescending,
      '',
      ...(filters as string[]),
    )
      .then(
        (result: IUsersResponse) => {
          setUsers(result.userList);
          if (result.page) {
            handleCurrentPage(result.page);
          }
          setIsEmptyResultMessage(result.userList.length === 0 ? true : false);
        },
        (error: AxiosError) => {
          if (error?.message !== 'canceled') history.push(getErrorPageUrl(error?.code ?? 500, 'admin'));
        },
      )
      .finally(() => {
        showLoader(false);
      });
  };

  const refreshUsersOnSuccessfullDeleteNotification = (success: boolean) => {
    if(history.location.pathname.includes(Paths.users) && success){
      setReload(state => !state);
    }
  }

  SignalRContext.useSignalREffect(
    CallbackMethodNames.UserDeletedInfo,
    (info) => refreshUsersOnSuccessfullDeleteNotification(info.succeeded),
    [],
  );

  useEffect(() => {
    const state: FilterStateGroup[] = [];
    if (Object.values(filter).some((e) => (_.isArray(e) ? e.length > 0 : e !== ''))) {
      state.push({
        name: '',
        checkedItems: [],
      });
    }
    if (filter.accountTypeName.length > 0) {
      state.push({
        name: 'Typ konta:',
        checkedItems: [...filter.accountTypeName].map((item) => getAccountTypeLabel(parseInt(item)) || ''),
      });
    }

    if (filter.examCommittee.length > 0) {
      state.push({
        name: 'Komisja:',
        checkedItems: [...filter.examCommittee].map((item) => getExamCommitteeNameFromIdString(parseInt(item))),
      });
    }

    if (filter.accountStatus.length > 0) {
      state.push({ name: 'Status konta:', checkedItems: [...filter.accountStatus] });
    }
    if (filter.status && filter.status.length > 0) {
      state.push({ name: 'Status:', checkedItems: [...filter.status] });
    }
    setFilterState(state);
  }, [filter]);

  const getExaminers = () => {
    getUsers(AccountTypeEnum.Examiner);
  };

  const uncheckAllUsersFilters = () => {
    setAccountTypeFilter([]);
    setExamCommitteeFilter([]);
    setAccountStatusFilter([]);
    setFilterState([]);
    setFilter(defaultUsersFilter);
    dispatch(filtersActions.resetFilter(infoType));
  };

  const handleUncheckExamCommitteeItem = useCallback(
    (value: string) => {
      const comitteeId = getExamCommitteeId(value).shift();
      if (comitteeId !== undefined) {
        const val = examCommitteeFilter.some((f) => f === comitteeId)
          ? examCommitteeFilter.filter((f) => f !== comitteeId)
          : [...examCommitteeFilter, comitteeId.toString()];
        setExamCommitteeFilter(val);
        setFilter((state) => ({
          ...state,
          examCommittee: val,
        }));
        dispatch(
          filtersActions.updateFilter({
            filterType: infoType,
            newValues: {
              filter: {
                examCommittee: val,
              },
              pageInfo: {
                currentPage: 0,
              },
            },
          }),
        );
      }
    },
    [examCommitteeFilter, infoType],
  );

  const handleUncheckAccountTypeItem = useCallback(
    (value: string) => {
      const accountTypeId = getAccountTypeId(value)?.toString() || '';
      const val = accountTypeFilter.some((f) => f === accountTypeId)
        ? accountTypeFilter.filter((f) => f !== accountTypeId)
        : [...accountTypeFilter, accountTypeId];
      setAccountTypeFilter(val);
      setFilter((state) => ({
        ...state,
        accountTypeName: val,
      }));
      dispatch(
        filtersActions.updateFilter({
          filterType: infoType,
          newValues: {
            filter: {
              accountTypeName: val,
            },
            pageInfo: {
              currentPage: 0,
            },
          },
        }),
      );
    },
    [accountTypeFilter, infoType],
  );

  const handleUncheckAccountStatus = useCallback(
    (value: string) => {
      const val = accountStatusFilter.some((f) => f === value)
        ? accountStatusFilter.filter((f) => f !== value)
        : [...accountStatusFilter, value];
      setAccountStatusFilter(val);
      setFilter((state) => ({
        ...state,
        accountStatus: val,
      }));
      dispatch(
        filtersActions.updateFilter({
          filterType: infoType,
          newValues: {
            filter: {
              accountStatus: val,
            },
            pageInfo: {
              currentPage: 0,
            },
          },
        }),
      );
    },
    [accountStatusFilter, infoType],
  );

  const handleUncheckStatus = useCallback(
    (value: string) => {
      const val = statusFilter.some((f) => f === value)
        ? statusFilter.filter((f) => f !== value)
        : [...statusFilter, value];
      setStatusFilter(val);
      setFilter((state) => ({
        ...state,
        status: val,
      }));
      dispatch(
        filtersActions.updateFilter({
          filterType: infoType,
          newValues: {
            filter: {
              status: val,
            },
            pageInfo: {
              currentPage: 0,
            },
          },
        }),
      );
    },
    [statusFilter, infoType],
  );

  const handleFilterChange = useCallback(
    (name: string, value: string | string[]) => {
      if (filter[name] === value) return;
      typeof value === 'string' && value.length === 1 ? setIsValidFilter(false) : setIsValidFilter(true);
      setFilter((state) => {
        if (state[name] !== value) {
          return {
            ...state,
            [name]: value,
          };
        } else {
          return state;
        }
      });
      dispatch(
        filtersActions.updateFilter({
          filterType: infoType,
          newValues: {
            filter: { [name]: value },
            pageInfo: {
              currentPage: 0,
            },
          },
        }),
      );

      const newValue = value;
      if (value && typeof value === 'string') {
        if (name === 'examCommittee' && !examCommitteeFilter.some((e) => e === value)) {
          setExamCommitteeFilter((arr: string[]) => [...arr, value]);
        }
        if (name === 'accountTypeName' && !accountTypeFilter.some((e) => e === value)) {
          setAccountTypeFilter((arr: string[]) => [...arr, value]);
        }
        if (name === 'accountStatus' && !accountStatusFilter.some((e) => e === value)) {
          setAccountStatusFilter((arr: string[]) => [...arr, value]);
        }
        if (name === 'status' && !statusFilter.some((e) => e === value)) {
          setStatusFilter((arr: string[]) => [...arr, value]);
        }
      } else if (value) {
        if (name === 'examCommittee') {
          setExamCommitteeFilter([...newValue]);
        }
        if (name === 'accountTypeName') {
          setAccountTypeFilter([...newValue]);
        }
        if (name === 'accountStatus') {
          setAccountStatusFilter([...newValue]);
        }
        if (name === 'status') {
          setStatusFilter([...newValue]);
        }
      }
    },
    [examCommitteeFilter, accountTypeFilter, accountStatusFilter, statusFilter, infoType, filter],
  );

  const getPages = useCallback(
    (_page?: number, _newPageSize?: number) => {
      dispatch(
        filtersActions.updateFilter({
          filterType: infoType,
          newValues: {
            pageInfo: {
              ...userDataInfo.filterData.pageInfo,
              currentPage: _page ? _page : 0,
              pageSize: _newPageSize ? _newPageSize : userDataInfo.filterData.pageInfo.pageSize,
            },
          },
        }),
      );
    },
    [userDataInfo.filterData.pageInfo, infoType],
  );

  const userAdded = () => {
    getUsers();
  };

  const uncheckUsersFilterItem = (item?: string) => {
    if (item) {
      const element = filterState.find((e) => e.checkedItems.find((x) => x === item));
      if (element?.name) {
        if (element.name === 'Typ konta:') {
          handleUncheckAccountTypeItem(item);
        }
        if (element.name === 'Komisja:') {
          handleUncheckExamCommitteeItem(item);
        }
        if (element.name === 'Status konta:') {
          handleUncheckAccountStatus(item);
        }
        if (element.name === 'Status:') {
          handleUncheckStatus(item);
        }
      }
    }
  };

  const getData = () => {
    if (examinersRoute) {
      setPreviousRoute('/examiners');
      getExaminers();
    } else {
      setPreviousRoute('/users');
      getUsers();
    }
  };

  useEffect(() => {
    getData();
  }, [
    userDataInfo.filterData.pageInfo.currentPage,
    userDataInfo.filterData.pageInfo.pageSize,
    filter,
    infoType,
    isDescending,
    sortByColumnId,
    examinersRoute,
    handleFilterChange,
    infoType,
    previousRoute,
    reload
  ]);

  const setIsDescendingOrder = (order: boolean) => {
    setIsDescending(order);
    dispatch(filtersActions.updateFilter({ filterType: infoType, newValues: { sort: { isDescending: order } } }));
  };

  const setSortByColumn = (column: UsersSortBy) => {
    setSortByColumnId(column);
    dispatch(filtersActions.updateFilter({ filterType: infoType, newValues: { sort: { sortByColumnId: column } } }));
  };

  const setSortByColumnExaminers = (column: ExaminersSortBy) => {
    setSortExaminersByColumnId(column);
    dispatch(filtersActions.updateFilter({ filterType: infoType, newValues: { sort: { sortByColumnId: column } } }));
  };

  return {
    userDataInfo,
    examinersRoute,
    isDescending,
    setIsDescending: setIsDescendingOrder,
    sortByColumnId,
    setSortByColumnId: setSortByColumn,
    sortExaminersByColumnId,
    setSortExaminersByColumnId: setSortByColumnExaminers,
    filterState,
    filter,
    pageInfo: userDataInfo.filterData.pageInfo,
    users,
    setUsers,
    getFilterValues,
    getUsers,
    getExaminers,
    uncheckAllUsersFilters,
    handleFilterChange,
    getPages,
    userAdded,
    uncheckUsersFilterItem,
  };
};
